import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, MessageSquare, Image as ImageIcon, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const GroupNotes = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [groupId]);

  const fetchData = async () => {
    try {
      const [groupRes, notesRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}`),
        axios.get(`${API}/groups/${groupId}/notes`)
      ]);
      setGroup(groupRes.data);
      setNotes(notesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/groups/${groupId}/notes?user_id=${user.id}&content=${encodeURIComponent(content)}${mediaUrl ? `&media_url=${encodeURIComponent(mediaUrl)}` : ''}`
      );
      toast.success('Note posted!');
      setContent('');
      setMediaUrl('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post note');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/group/${groupId}`)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900" data-testid="notes-title">
              Group Board
            </h1>
            <p className="text-gray-600 mt-1">Share updates and media with {group.name}</p>
          </div>

          {/* Post Form */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <Textarea
                placeholder="Share an update, milestone, or idea..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="rounded-xl min-h-[100px]"
                data-testid="note-content-input"
              />
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Input
                    placeholder="Add image URL (optional)"
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    className="rounded-xl"
                    data-testid="media-url-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-note-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Posting...' : 'Post'}
                </Button>
              </div>
            </form>
          </div>

          {/* Notes Feed */}
          <div className="space-y-4">
            {notes.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-200">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No posts yet</h3>
                <p className="text-gray-600">Be the first to share something!</p>
              </div>
            ) : (
              notes.map((note, index) => (
                <motion.div
                  key={note.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
                  data-testid={`note-${index}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-700 font-semibold">
                        {note.user_name[0]}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{note.user_name}</p>
                        {note.user_id === user.id && (
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {new Date(note.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-900 mb-3 whitespace-pre-wrap">{note.content}</p>

                  {note.media_url && (
                    <div className="rounded-xl overflow-hidden border border-gray-200">
                      <img
                        src={note.media_url}
                        alt="Posted media"
                        className="w-full h-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GroupNotes;
